import styled from '@emotion/styled';
import { defaultContainerSize, mq } from '../../../styles/mixins';
import { css } from '@emotion/react';
import {
  Header2,
  ParagraphL,
  ParagraphM,
} from '../../core/Typography/Typography';

export const Section = styled.section`
  width: 100%;
  position: relative;
  z-index: 0;
  margin-bottom: 4rem;

  ${mq.small(css`
    margin-bottom: 5rem;
  `)};

  ${mq.xLarge(css`
    margin-bottom: 7.5rem;
  `)};
`;

export const Content = styled.div`
  display: flex;
  justify-content: space-around;
  position: relative;
  flex-wrap: wrap;

  ${mq.medium(css`
    flex-wrap: nowrap;
  `)};
`;

export const Inner = styled.div`
  ${defaultContainerSize};
  padding-top: 2.5rem;
  padding-bottom: 5rem;

  ${mq.small(css`
    padding-top: 4rem;
    padding-bottom: 4rem;
  `)};

  ${mq.xLarge(css`
    padding-top: 7.5rem;
    padding-bottom: 8.75rem;
  `)};
`;

export const Author = styled.div`
  display: flex;
  margin-top: 1.5rem;

  ${mq.xLarge(css`
    margin-top: 4rem;
  `)};
`;

export const AuthorPicture = styled.div`
  height: 3rem;
  width: 3rem;
  min-width: 3rem;
  min-height: 3rem;
  border-radius: 3rem;
  position: relative;

  ${mq.xLarge(css`
    height: 3.5rem;
    width: 3.5rem;
    min-width: 3.5rem;
    min-height: 3.5rem;
    border-radius: 3.5rem;
  `)};
`;

export const AuthorDetails = styled.div`
  margin-left: 0.75rem;
  display: flex;
  flex-wrap: wrap;

  ${mq.small(css`
    margin-left: 1rem;
  `)};
`;

export const AuthorName = styled.p`
  margin: 0;
  font-size: 1.25rem;
  line-height: 2rem;
  color: ${({ theme }) => theme.colors.core_black};
  flex: 0 0 100%;

  ${mq.xLarge(css`
    font-size: 1.5rem;
  `)};
`;

export const AuthorPosition = styled.p`
  margin: 0;
  font-size: 0.75rem;
  line-height: 0.875rem;
  font-family: ${({ theme }) => theme.font.nunito};
  font-weight: 300;
  color: ${({ theme }) => theme.colors.darker_grey};
  flex: 0 0 100%;

  ${mq.xLarge(css`
    font-size: 1rem;
    line-height: 2rem;
  `)};
`;

export const Paragraph = styled.p`
  ${ParagraphM};
  font-weight: 400;
  margin: 0;
  letter-spacing: 0.0125rem;
  color: ${({ theme }) => theme.colors.core_white};

  ${mq.medium(css`
    letter-spacing: 0;
  `)};

  ${mq.large(css`
    ${ParagraphL};
    font-weight: 400;
  `)};
`;

export const Bold = styled.span`
  font-weight: 700;
`;

export const Title = styled.h3`
  ${Header2};
  color: ${({ theme }) => theme.colors.black};
  margin: 0 0 1rem 0;
  display: block;
  text-align: center;

  ${mq.medium(css`
    text-align: left;
    margin-bottom: 4rem;
    font-size: 3rem;
    line-height: 3.5rem;
  `)};
`;

export const Button = styled.button`
  position: relative;
  z-index: 999;
  align-self: flex-start;
  font-family: ${({ theme }) => theme.font.nunito};
  font-weight: 300;
  font-size: 1rem;
  line-height: 1.5rem;
  color: ${({ theme }) => theme.colors.grey};
  text-decoration: underline;
  background: transparent;
  border: none;
  margin-top: 1rem;
  padding: 0.5rem 0;

  ${mq.medium(css`
    font-size: 1.125rem;
    line-height: 2.25rem;
    margin-top: 2rem;
  `)};

  ${mq.xLarge(css`
    display: none;
  `)};
`;

type QuoteIconType = {
  type: 'left' | 'right';
};

export const QuoteIcon = styled.div<QuoteIconType>`
  min-width: 2.625rem;
  min-height: 2rem;
  position: relative;
  width: 100%;

  ${mq.medium(css`
    width: auto;
  `)};

  &:after {
    content: '';
    background: url("data:image/svg+xml,%3Csvg width='37' height='28' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M28.2095 14.7778c0 1.3827.8627 2.8518 2.588 4.4074 1.7254 1.5555 2.5881 2.5926 2.5881 3.1111 0 3.8025-1.8117 5.7037-5.4349 5.7037-2.0704 0-3.9683-.6049-5.6937-1.8148-1.7253-1.3827-2.588-3.4568-2.588-6.2222 0-2.9383 1.5528-7 4.6585-12.18522C27.6056 2.59259 31.4014 0 35.7148 0c.6901 0 1.0352.172841 1.0352.51852-5.6937 5.7037-8.5405 10.45678-8.5405 14.25928zm-19.66901-.2593c0 1.3827.86268 2.8519 2.58801 4.4074 1.7254 1.5556 2.588 2.679 2.588 3.3704 0 3.8025-1.8978 5.7037-5.69361 5.7037-2.07043 0-3.96831-.6049-5.69366-1.8148C.776409 24.9753 0 22.9012 0 19.963c0-2.9383 1.63908-7 4.91725-12.18522C8.19542 2.59259 11.8187 0 15.787 0c.6901 0 1.0352.259259 1.0352.777776C11.3011 7 8.54049 11.5802 8.54049 14.5185z' fill='%23212121'/%3E%3C/svg%3E")
      no-repeat center center;
    min-width: 2rem;
    min-height: 1.5rem;
    background-size: 100% 100%;
    position: absolute;

    ${mq.medium(css`
      min-width: 2.625rem;
      min-height: 2rem;
    `)};
  }

  ${({ type }) => {
    switch (type) {
      case 'left':
        return css`
          margin-bottom: 1rem;
          ${mq.medium(css`
            margin-right: 1.5rem;
            margin-bottom: 0;
          `)};
          &:after {
            left: 0;
            top: 0;
          }
        `;
      case 'right':
        return css`
          margin-top: 1rem;
          ${mq.medium(css`
            margin-left: 1.5rem;
            margin-top: 0;
          `)};
          &:after {
            bottom: 0;
            right: 0;
            transform: rotate(180deg);
          }
        `;
    }
  }}
`;
